import { useState, useEffect, useCallback, useRef } from 'react';

interface ParallaxOffset {
  x: number;
  y: number;
}

/**
 * useParallax — returns { x, y } offset (in px) based on
 * deviceorientation (mobile) or mousemove (desktop).
 * Range: [-maxOffset, +maxOffset]
 */
export function useParallax(maxOffset = 10, enabled = true): ParallaxOffset {
  const [offset, setOffset] = useState<ParallaxOffset>({ x: 0, y: 0 });
  const targetRef = useRef<ParallaxOffset>({ x: 0, y: 0 });
  const rafRef = useRef<number>(0);

  // Smooth lerp animation
  const animate = useCallback(() => {
    setOffset(prev => ({
      x: prev.x + (targetRef.current.x - prev.x) * 0.08,
      y: prev.y + (targetRef.current.y - prev.y) * 0.08,
    }));
    rafRef.current = requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    if (!enabled) return;

    rafRef.current = requestAnimationFrame(animate);

    // Try device orientation first (mobile tilt)
    let hasOrientation = false;
    const handleOrientation = (e: DeviceOrientationEvent) => {
      hasOrientation = true;
      const gamma = e.gamma || 0; // left/right tilt (-90..90)
      const beta = e.beta || 0;   // front/back tilt (-180..180)
      targetRef.current = {
        x: (gamma / 45) * maxOffset,
        y: ((beta - 45) / 45) * maxOffset,
      };
    };

    // Desktop fallback: mouse position
    const handleMouse = (e: MouseEvent) => {
      if (hasOrientation) return;
      const cx = window.innerWidth / 2;
      const cy = window.innerHeight / 2;
      targetRef.current = {
        x: ((e.clientX - cx) / cx) * maxOffset,
        y: ((e.clientY - cy) / cy) * maxOffset,
      };
    };

    window.addEventListener('deviceorientation', handleOrientation);
    window.addEventListener('mousemove', handleMouse);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('deviceorientation', handleOrientation);
      window.removeEventListener('mousemove', handleMouse);
    };
  }, [enabled, maxOffset, animate]);

  return offset;
}

export default useParallax;
